
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SaveLeadInputSchema, type SaveLeadInput } from "@/ai/flows/lead-summarizer-schemas";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/firebase/provider";
import { incrementBookingRequest } from "@/lib/firebase/services/profilesService";
import { isBookingIntent } from "@/lib/leads/leadIntents";
import { useEffect, useState } from "react";
import { Loader2, MessageCircle, CheckCircle2 } from "lucide-react";

// The form only owns these fields; profileOwnerId and intent are added at
// submit time. Validating against the full SaveLeadInputSchema silently fails
// on the missing profileOwnerId — no field to render its error — which makes
// the submit button look dead.
// The form only owns these fields; profileOwnerId and intent are added at
// submit time. Validating against the full SaveLeadInputSchema silently fails
// on the missing profileOwnerId — no field to render its error — which makes
// the submit button look dead.
const formSchema = SaveLeadInputSchema.omit({ profileOwnerId: true }).extend({
  name: z.string().min(1, "Please tell us your name."),
  message: z.string().min(1, "Please write a short message."),
});
type LeadFormValues = z.infer<typeof formSchema>;

interface LeadInquiryFormProps {
  profileOwnerId: string;
  profileOwnerName: string;
  profileOwnerWhatsapp?: string;
  initialMessage?: string;
  inquiryIntent?: string;
  title?: string;
  description?: string;
}

export function LeadInquiryForm({
  profileOwnerId,
  profileOwnerName,
  profileOwnerWhatsapp,
  initialMessage,
  inquiryIntent = "general-inquiry",
  title,
  description,
}: LeadInquiryFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [submittedContact, setSubmittedContact] = useState<{ name: string; email: string; whatsappNumber: string } | null>(null);
  const { toast } = useToast();
  const { auth } = useFirebase();

  const isDirectContactIntent =
    inquiryIntent === "jobseeker-cv-request" || inquiryIntent === "jobseeker-interview-invite";

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(formSchema),
    disabled: isLoading,
    defaultValues: {
      name: "",
      email: "",
      message: initialMessage || "",
      whatsappNumber: "",
    },
  });

  useEffect(() => {
    const next = String(initialMessage || "").trim();
    if (!next) return;
    form.setValue("message", next, { shouldDirty: false, shouldValidate: true });
  }, [form, initialMessage]);

  async function onSubmit(values: LeadFormValues) {
    setIsLoading(true);
    try {
      const response = await fetch("/api/save-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          profileOwnerId: profileOwnerId,
          intent: inquiryIntent,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to send inquiry");
      }
      // Only count genuine booking/order attempts. General, product and hiring
      // inquiries must not inflate the public bookingRequests analytics.
      if (isBookingIntent(inquiryIntent)) {
        try {
          await incrementBookingRequest(profileOwnerId, auth.currentUser?.uid ?? null);
        } catch (err) {
          // Non-blocking
          console.error("Failed to track booking request submission", err);
        }
      }
      setIsSent(true);
      setSubmittedContact({ name: values.name, email: values.email || "", whatsappNumber: values.whatsappNumber || "" });
      toast({
        title: "Inquiry Sent!",
        description: `Your message has been sent to ${profileOwnerName}.`,
      });
      form.reset();
    } catch (error) {
      console.error("Failed to send inquiry:", error);
      toast({
        title: "Error",
        description: "Failed to send your inquiry. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }
  
  if (isSent) {
    if (isDirectContactIntent && profileOwnerWhatsapp && submittedContact) {
      const intentLabel =
        inquiryIntent === "jobseeker-cv-request" ? "request your CV" : "invite you to an interview";
      const contactParts = [
        submittedContact.email ? `email: ${submittedContact.email}` : null,
        submittedContact.whatsappNumber ? `WhatsApp: ${submittedContact.whatsappNumber}` : null,
      ]
        .filter(Boolean)
        .join(" / ");
      const waMessage = [
        `Hi ${profileOwnerName}, my name is ${submittedContact.name} and I found your profile on Zamzi Connect.`,
        `I would like to ${intentLabel}.`,
        contactParts ? `You can reach me on ${contactParts}.` : "",
      ]
        .filter(Boolean)
        .join(" ");
      const waNumber = profileOwnerWhatsapp.replace(/\D/g, "");
      const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;
      const intentHeading =
        inquiryIntent === "jobseeker-cv-request" ? "CV Request Sent" : "Interview Invite Sent";
      const intentBody =
        inquiryIntent === "jobseeker-cv-request"
          ? `Tap below to message ${profileOwnerName} directly on WhatsApp with your contact details so they can send you their CV.`
          : `Tap below to message ${profileOwnerName} directly on WhatsApp with your contact details to arrange the interview.`;
      return (
        <div className="mt-8 rounded-2xl border border-green-200 bg-green-50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
            <div>
              <p className="font-semibold text-green-900">{intentHeading}</p>
              <p className="text-sm text-green-800 mt-0.5">{intentBody}</p>
            </div>
          </div>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            Message {profileOwnerName} on WhatsApp
          </a>
          <p className="text-xs text-center text-green-700">
            This opens WhatsApp with your contact details pre-filled — {profileOwnerName} can reply directly to you.
          </p>
        </div>
      );
    }

    return (
      <div className="text-center p-8 border rounded-lg bg-secondary/50">
        <h3 className="text-lg font-semibold text-primary">Thank You!</h3>
        <p className="text-muted-foreground mt-2">Your inquiry has been successfully sent.</p>
      </div>
    );
  }

  return (
    <Card className="shadow-lg mt-8">
      <CardHeader>
        <CardTitle className="text-xl font-bold" style={{ color: "#001489" }}>
          {title || `Send an Inquiry to ${profileOwnerName}`}
        </CardTitle>
        <CardDescription>{description || "Interested in connecting? Fill out the form below."}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          {/** Move the form tag inside Form, not the other way around **/}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field}  />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Email (Optional)</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field}  />
                  </FormControl>
                  <FormDescription>
                    Your email is kept private and will appear as a "Contact via Email" button to the profile owner.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={`Hi ${profileOwnerName}, I'm interested in...`}
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="whatsappNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WhatsApp Number (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 0721234567" {...field} />
                  </FormControl>
                  <FormDescription>
                    Your number is kept private and will appear as a "Contact on WhatsApp" button to the profile owner.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" style={{ backgroundColor: "#001489" }} >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Inquiry"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
